import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../../../firebase';
import { Calendar, CheckCircle, XCircle, Clock, Gift } from 'lucide-react';
import "./DailyTasks.css";
import { API_ENDPOINTS } from "../../../config/api";

const DailyTasks = () => {
  const [user, setUser] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [taskCode, setTaskCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        await loadInvestments(u.email);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const loadInvestments = async (email) => {
    try {
      const res = await axios.get(API_ENDPOINTS.GET_INVESTMENTS(email));
      if (res.data.success) {
        const active = res.data.investments.filter(i => i.status === 'active');
        setInvestments(active);
        if (active.length > 0 && !selected) setSelected(active[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected || taskCode.length !== 8) return;

    setSubmitting(true);
    setMessage('');

    try {
      const res = await axios.post(API_ENDPOINTS.COMPLETE_DAILY_TASK(selected.id), {
        email: user.email,
        taskCode
      });

      if (res.data.success) {
        setMessage(`✅ ${res.data.message}`);
        setTaskCode('');
        await loadInvestments(user.email);
      }
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.message || 'Failed'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="daily-tasks-container">Loading...</div>;
  if (!selected) {
    return (
      <div className="daily-tasks-container">
        <Gift size={48} />
        <h2>No Active Investments</h2>
        <p>Make an investment to start your 30-day journey!</p>
      </div>
    );
  }

  return (
    <div className="daily-tasks-container">
      <div className="dt-header">
        <Calendar size={32} />
        <div>
          <h2>Daily Tasks</h2>
          <p>Complete 30 days to double your money</p>
        </div>
      </div>

      <div className="inv-selector">
        {investments.map(inv => (
          <button
            key={inv.id}
            className={`inv-btn ${selected.id === inv.id ? 'active' : ''}`}
            onClick={() => setSelected(inv)}
          >
            ${inv.amount} → ${inv.totalReturn}
          </button>
        ))}
      </div>

      <div className="progress-section">
        <div className="stats">
          <div className="stat"><span>Completed</span><strong>{selected.completedDays}</strong></div>
          <div className="stat"><span>Remaining</span><strong>{selected.daysRemaining}</strong></div>
          <div className="stat missed"><span>Missed</span><strong>{selected.missedDays || 0}</strong></div>
        </div>
        <div className="progress-bar">
          <div className="fill" style={{ width: `${(selected.completedDays / 30) * 100}%` }}></div>
        </div>
        <p>{selected.completedDays} / 30 days</p>
        {selected.extensionDays > 0 && (
          <p className="extension">⚠️ +{selected.extensionDays} extension day(s)</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="task-form">
        <h3>Today's Task</h3>
        <input
          type="text"
          value={taskCode}
          onChange={(e) => setTaskCode(e.target.value.toUpperCase())}
          placeholder="Enter 8-character code"
          maxLength={8}
          required
        />
        {message && <div className={`msg ${message.includes('✅') ? 'success' : 'error'}`}>{message}</div>}
        <button type="submit" disabled={submitting || taskCode.length !== 8}>
          {submitting ? 'Submitting...' : 'Complete Task'}
        </button>
      </form>

      <div className="days-grid">
        <h3>Daily Progress</h3>
        <div className="grid">
          {selected.dailyTasks?.map((task, i) => {
            const isPast = new Date(task.date) < new Date();
            return (
              <div key={i} className={`day ${task.completed ? 'done' : ''} ${!task.completed && isPast ? 'missed' : ''}`}>
                <div className="day-num">Day {task.dayNumber}</div>
                <div className="day-date">{new Date(task.date).toLocaleDateString()}</div>
                {task.completed ? <CheckCircle size={18} /> : isPast ? <XCircle size={18} /> : <Clock size={18} />}
              </div>
            );
          })}
        </div>
      </div>

      {selected.isComplete && (
        <div className="complete-notice">
          <CheckCircle size={48} />
          <h3>🎉 All 30 Days Complete!</h3>
          <p>Check your email for the claim code to double your money.</p>
        </div>
      )}
    </div>
  );
};

export default DailyTasks;